import {
  Analyze,
  Chat,
  Design,
  Devices,
  Export,
  Invite,
} from "@/components/icons"
import { Fragment, ReactNode } from "react"
import DefaultLayout from "@/layouts/DefaultLayout"
import Container from "@/components/Container"
import Button from "@/components/Button"
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react"
import useUser from "@/hooks/useUser"
import { captureEvent } from "@/lib/posthog"
import FeatureContainer from "@/components/features/FeatureContainer"
import AllGames from "@/components/features/AllGames"
import Events from "@/components/features/Events"
import Variations from "@/components/features/Variations"
import Share from "@/components/features/Share"
import { BiSolidQuoteAltRight } from "react-icons/bi"
import { PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import { faqs } from "@/constants"
import clsx from "clsx"
import CTA from "@/components/CTA"
import appPhone from "@/assets/img/app-phone.png?as=picture&w=244;488;284;568&format=png;webp;avif&imagetools"
import appDesktop from "@/assets/img/app-desktop.png?as=picture&w=768;1536&format=png;webp;avif&imagetools"
import martinKlubeck from "@/assets/img/martin-klubeck.png?as=picture&w=64;128&format=png;webp;avif&imagetools"
import lucasAnderson from "@/assets/img/lucas-anderson.png?as=picture&w=64;128&format=png;webp;avif&imagetools"
import Image from "@/components/Image"

const features = [
  {
    title: "Works everywhere",
    body: "From your desktop computer to smartphones and tablets, ChessDrop is the truly mobile solution.",
    icon: Devices,
  },
  {
    title: "Analyze your games",
    body: "Use the built-in chess engine to check for great moves, missed opportunities, and even those painful blunders!",
    icon: Analyze,
  },
  {
    title: "Comments & variations",
    body: "Annotate your games with comments, variation trees and move icons. Great for both preparation and post-game analysis.",
    icon: Chat,
  },
  {
    title: "Beautiful design",
    body: "Created by the team that has built critically acclaimed products for Google, ChessDrop was been designed for intuitive ease of use.",
    icon: Design,
  },
  {
    title: "Invite your opponents",
    body: "Save your opponent some recording time by letting them import a copy of your game into their own ChessDrop account.",
    icon: Invite,
  },
  {
    title: "Export your games",
    body: "With PGN export facilities, you can store your games in ChessDrop with full confidence that your data is never locked within our system.",
    icon: Export,
  },
]

const testimonials = [
  {
    testimonial:
      "ChessDrop is my new go-to tool for the online portion of my chess class. Getting Chess in the schools in our district just became much easier since we have a means for teaching online. The price is extremely reasonable and the interface just keeps getting better. Love the customer service and responsiveness of the design team. Thanks ChessDrop — I’m excited to see where this tool will take us!",
    name: "Martin Klubeck",
    title: "Chess Teacher, Club Director, Coach, University of Notre Dame",
    image: martinKlubeck,
  },
  {
    testimonial:
      "Between rounds in a tournament, my players whip out their phones to put their game into ChessDrop. We then open them on a laptop or project them onto a screen for instant analysis. If we get stuck, we turn on engine analysis for clarification. ChessDrop has become my database of choice for student games and my go-to resource for teaching from them.",
    name: "Lucas Anderson",
    title: "Librarian & Chess Coach, Awty International School, Houston, Texas",
    image: lucasAnderson,
  },
]

const IndexPage = () => {
  const { isAuthenticated } = useUser()

  return (
    <>
      <section className="overflow-hidden border-b border-gray-200 bg-gray-50 sm:border-b-0">
        <Container className="py-16 sm:grid sm:grid-cols-12 sm:items-center sm:gap-x-16 sm:py-24">
          <div className="flex flex-col items-center sm:col-span-7 sm:items-start">
            <h1 className="text-center text-4xl font-extrabold tracking-tighter text-gray-900 sm:text-left sm:text-5xl sm:leading-none md:text-6xl">
              Your personal <br className="hidden xl:inline" /> online chess
              database
            </h1>
            <p className="mx-auto mt-4 max-w-md text-center text-lg text-gray-600 sm:mx-0 sm:text-left md:mt-6 md:text-xl">
              Finally, an online place to store your chess games and share your
              favorites with your friends!
            </p>
            <div className="mt-8 flex justify-center gap-x-2 sm:justify-start md:mt-12">
              {isAuthenticated ? (
                <Button href="/games" size="xl">
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    href="/try"
                    size="xl"
                    onClick={() => captureEvent("guest_game_started", { source: "try_it_cta" })}
                  >
                    Try it!
                  </Button>
                  <Button href="/signup" style="tertiary" size="xl">
                    Sign up
                  </Button>
                  <Button href="/login" style="tertiary" size="xl">
                    Log in
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="relative mt-16 h-80 sm:col-span-5 sm:mt-0 sm:h-auto md:pb-14">
            <div className="ml-24 hidden h-auto w-[776px] overflow-hidden rounded-3xl border-4 border-gray-900 bg-white shadow-2xl md:block">
              <Image
                image={appDesktop}
                alt=""
                width={768}
                height={512}
                className="hidden size-full object-cover select-none md:block"
                sizes="(max-width: 767px) 0px, 768px"
                priority
              />
            </div>
            <div className="mx-auto h-auto w-[284px] md:absolute md:top-18 md:left-0 md:w-[244px]">
              <Image
                image={appPhone}
                alt=""
                width={244}
                height={498}
                className="size-full object-contain select-none"
                sizes="(min-width: 768px) 244px, 284px"
                priority
              />
            </div>
          </div>
        </Container>
      </section>

      <Container
        tag="section"
        className="space-y-12 py-16 sm:space-y-24 sm:py-24"
      >
        <div className="mx-auto max-w-2xl">
          <h2 className="text-center text-3xl font-bold tracking-tighter text-gray-900 sm:text-4xl lg:text-5xl">
            Store your games privately. <br className="hidden sm:inline" />{" "}
            Share your best with the world.
          </h2>
          <p className="mt-4 text-center text-lg text-gray-600 sm:mt-5 sm:text-xl">
            ChessDrop is an online home for all your chess games — available
            anytime, anywhere and from any device. Use your smartphone to
            analyze and record your games at tournaments. And when you like,
            share your best one with your friends in WhatsApp, Twitter, etc.
          </p>
        </div>

        <FeatureContainer illustration={<AllGames />}>
          <FeatureContainer.Heading>
            All your games in one place
          </FeatureContainer.Heading>
          <FeatureContainer.Paragraph>
            The days of having your games spread across an endless number of PGN
            files, iPad apps and paper are over. With ChessDrop, all your games
            are available in one place — accessible anytime and anywhere.
          </FeatureContainer.Paragraph>
        </FeatureContainer>

        <FeatureContainer illustration={<Events />}>
          <FeatureContainer.Heading>Organize by event</FeatureContainer.Heading>
          <FeatureContainer.Paragraph>
            ChessDrop can organize your games by event, providing convenient
            access to both sharing and exporting. When we go to tournaments, for
            example, we’ll send the event link to our son’s coach, who can
            follow along from afar.
          </FeatureContainer.Paragraph>
        </FeatureContainer>

        <FeatureContainer illustration={<Variations />}>
          <FeatureContainer.Heading>
            Comments & variations
          </FeatureContainer.Heading>
          <FeatureContainer.Paragraph>
            ChessDrop allows you to annotate your games with comments,
            variations and move icons. This is super-useful for both
            pre-tournament preparation, as well as post-game analysis with a
            coach. In fact, these features are what makes ChessDrop so
            attractive to both individuals and educational organizations!
          </FeatureContainer.Paragraph>
        </FeatureContainer>

        <FeatureContainer illustration={<Share />}>
          <FeatureContainer.Heading>
            Share your games and events
          </FeatureContainer.Heading>
          <div className="space-y-5">
            <FeatureContainer.Paragraph>
              ChessDrop is private, by design. Each game and event in your
              account will be assigned a unique, hard-to-guess URL—providing
              privacy through obscurity. But since these URLs are normal links,
              you can share them whenever you like! For example, if you share
              your game with an opponent, he or she will be able to add a copy
              of the game to their own ChessDrop account, saving entry time.
            </FeatureContainer.Paragraph>
            <FeatureContainer.Paragraph>
              As another example, say you’re at a tournament and want to share
              your games with your coach. After entering your first game, just
              send your coach he event link created by ChessDrop, and he or she
              will have access to your current and future games recorded at that
              event!
            </FeatureContainer.Paragraph>
          </div>
        </FeatureContainer>
      </Container>

      <Container
        tag="section"
        className="grid gap-x-8 gap-y-10 py-16 sm:grid-cols-2 sm:gap-y-16 sm:py-24 lg:grid-cols-3"
      >
        {features.map(({ title, body, icon: Icon }) => (
          <div key={title}>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 sm:h-14 sm:w-14">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-200 sm:h-10 sm:w-10">
                <Icon className="h-5 w-5 text-gray-200 sm:h-6 sm:w-6" />
              </div>
            </div>
            <h3 className="mt-4 text-lg font-bold text-gray-900 sm:mt-5 sm:text-xl">
              {title}
            </h3>
            <p className="mt-1 text-base text-gray-600 sm:mt-2">{body}</p>
          </div>
        ))}
      </Container>

      <Container
        tag="section"
        className="space-y-12 py-16 sm:space-y-16 sm:py-24 lg:space-y-24"
      >
        <h2 className="text-center text-3xl font-bold tracking-tighter text-balance text-gray-900 sm:text-4xl lg:text-5xl">
          Praise from chess teachers and coaches
        </h2>

        <div className="grid gap-y-16 lg:grid-cols-2 lg:gap-x-24">
          {testimonials.map(({ testimonial, name, title, image }) => (
            <figure
              key={name}
              className="mx-auto flex flex-col justify-between sm:max-w-md lg:max-w-none"
            >
              <blockquote className="relative">
                <BiSolidQuoteAltRight className="absolute -top-8 -left-8 hidden h-24 w-24 text-gray-900/5 lg:block" />
                <p className="relative text-base text-gray-600 sm:text-lg md:text-xl md:leading-8">
                  {testimonial}
                </p>
              </blockquote>
              <div className="mt-6 flex items-center space-x-4 sm:mt-8">
                <div className="size-14 shrink-0 overflow-hidden rounded-full bg-gray-100 sm:size-16">
                  <Image
                    image={image}
                    alt=""
                    width={64}
                    height={64}
                    className="size-full object-contain"
                    sizes="64px"
                  />
                </div>
                <figcaption>
                  <span className="block text-base font-bold text-gray-900 sm:text-lg">
                    {name}
                  </span>
                  <span className="block text-sm text-gray-600 sm:text-base">
                    {title}
                  </span>
                </figcaption>
              </div>
            </figure>
          ))}
        </div>
      </Container>

      <Container tag="section" className="py-16 sm:py-24">
        <h2 className="text-center text-3xl font-bold tracking-tighter text-balance text-gray-900 sm:text-4xl lg:text-5xl">
          Frequently asked questions
        </h2>

        <ul className="mx-auto mt-12 max-w-3xl divide-y divide-gray-200 border-y border-gray-200 sm:mt-16">
          {faqs.map(({ question, answer }) => (
            <Disclosure key={question} as="li">
              {({ open }) => (
                <>
                  <DisclosureButton
                    className={clsx(
                      "group flex w-full items-start justify-between gap-4 text-left text-gray-900",
                      open ? "pt-6 pb-2 sm:pt-8" : "py-6 sm:py-8"
                    )}
                  >
                    <h3 className="text-lg font-medium text-gray-900">
                      {question}
                    </h3>
                    {open ? (
                      <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-gray-400" />
                    ) : (
                      <PlusCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-gray-400" />
                    )}
                  </DisclosureButton>
                  <DisclosurePanel as={Fragment}>
                    <p className="pb-6 text-base text-gray-600 sm:pb-8">
                      {answer}
                    </p>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
          ))}
        </ul>
      </Container>

      <CTA />
    </>
  )
}

export default IndexPage

IndexPage.layout = (page: ReactNode) => (
  <DefaultLayout headerClassName="bg-gray-50">{page}</DefaultLayout>
)
